import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import {
  Select,
  FormControl,
  MenuItem,
  OutlinedInput,
  Typography,
  ListSubheader,
} from "@material-ui/core";
import { ChevronRight, Check } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import { GET_OFFICES_SELECT } from "./queries";
import useOffice from "components/Offices/useOffice";
import ErrorMessageCustom from "components/ErrorMessageCustom";

const useStyles = makeStyles((theme) => ({
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.grey[100],
    cursor: "pointer",
    pointerEvents: "auto",
    userSelect: "none",
    position: "relative",
    top: 0,
    "&:hover": {
      backgroundColor: theme.palette.grey[200],
    },
  },
  groupHeaderCollapsed: {
    "& $chevronIcon": {
      transform: "rotate(0deg)",
    },
  },
  groupHeaderExpanded: {
    "& $chevronIcon": {
      transform: "rotate(90deg)",
    },
  },
  chevronIcon: {
    transition: "transform 0.2s ease-in-out",
    fontSize: "1.2rem",
  },
  nestedMenuItem: {
    paddingLeft: theme.spacing(4),
    transition: "background-color 0.2s ease-in-out",
  },
  selectedMenuItem: {
    backgroundColor: `${theme.palette.primary.light}20 !important`,
    fontWeight: 600,
    "& .MuiListItemIcon-root": {
      minWidth: "auto",
      marginRight: theme.spacing(1),
    },
  },
  checkIcon: {
    fontSize: "1rem",
    color: theme.palette.primary.main,
  },
}));

const findOffice = (event, offices) => {
  return offices.find((office) => office.id === event.target.value);
};

const OfficeSelect = ({ htmlId }) => {
  const classes = useStyles();
  const [officeSelectValue, setOfficeSelectValue, clear] = useOffice();
  const { loading, error, data } = useQuery(GET_OFFICES_SELECT);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectOpen, setSelectOpen] = useState(false);

  // Process offices to create hierarchical structure
  // Must be called before any early returns to follow React Hooks rules
  const offices = data?.offices?.data;
  const { standaloneOffices, groupedOffices, hasGroupings } = useMemo(() => {
    if (!offices)
      return { standaloneOffices: [], groupedOffices: {}, hasGroupings: false };

    const standalone = [];
    const grouped = {};
    let hasGroups = false;

    offices.forEach((office) => {
      const groupName = office.regroupement_agence?.trim();
      if (groupName) {
        hasGroups = true;
        if (!grouped[groupName]) {
          grouped[groupName] = [];
        }
        grouped[groupName].push(office);
      } else {
        standalone.push(office);
      }
    });

    // Sort grouped offices by name within each group (case-insensitive, French locale)
    Object.keys(grouped).forEach((groupName) => {
      grouped[groupName].sort((a, b) =>
        a.name.localeCompare(b.name, "fr", { sensitivity: "base" })
      );
    });

    // Sort standalone offices by name (case-insensitive, French locale)
    standalone.sort((a, b) =>
      a.name.localeCompare(b.name, "fr", { sensitivity: "base" })
    );

    return {
      standaloneOffices: standalone,
      groupedOffices: grouped,
      hasGroupings: hasGroups,
    };
  }, [offices]);

  // Find which group contains the selected office
  const selectedOfficeGroup = useMemo(() => {
    if (!officeSelectValue?.id || !hasGroupings) return null;

    for (const [groupName, groupOffices] of Object.entries(groupedOffices)) {
      if (groupOffices.some((office) => office.id === officeSelectValue.id)) {
        return groupName;
      }
    }
    return null;
  }, [officeSelectValue, groupedOffices, hasGroupings]);

  // Auto-expand group containing selected office when dropdown opens
  useEffect(() => {
    if (
      selectOpen &&
      selectedOfficeGroup &&
      !expandedGroups[selectedOfficeGroup]
    ) {
      setExpandedGroups((prev) => ({
        ...prev,
        [selectedOfficeGroup]: true,
      }));
    }
  }, [selectOpen, selectedOfficeGroup]);

  // Get the display name for the selected office
  const selectedOfficeName = useMemo(() => {
    if (!officeSelectValue?.id || !offices) return "";
    const office = offices.find((o) => o.id === officeSelectValue.id);
    return office?.name || "";
  }, [officeSelectValue, offices]);

  // Early returns after all hooks
  if (error) {
    return <ErrorMessageCustom error="Liste des agences indisponible" />;
  }

  if (loading) {
    return null;
  }

  const hasManyOffices = offices && offices.length > 1;
  const hasSelectedOffice = officeSelectValue && officeSelectValue.id;
  const hasOneOffice = offices && offices.length > 0;

  if (offices && offices.length === 0) {
    clear();
  }
  if (!hasManyOffices && !hasSelectedOffice && hasOneOffice) {
    setOfficeSelectValue(offices[0]);
  }

  const toggleGroup = (groupName, event) => {
    // Prevent the Select from closing
    event.stopPropagation();
    event.preventDefault();

    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const renderMenuItems = () => {
    const items = [];
    // Sort group names alphabetically (case-insensitive, French locale)
    const sortedGroupNames = Object.keys(groupedOffices).sort((a, b) =>
      a.localeCompare(b, "fr", { sensitivity: "base" })
    );
    const selectedId = officeSelectValue?.id;

    // If no groupings exist, render flat list (sorted)
    if (!hasGroupings) {
      return offices
        .slice()
        .sort((a, b) =>
          a.name.localeCompare(b.name, "fr", { sensitivity: "base" })
        )
        .map((office) => {
          const isSelected = office.id === selectedId;
          return (
            <MenuItem
              key={office.id}
              value={office.id}
              className={isSelected ? classes.selectedMenuItem : ""}
            >
              {isSelected && <Check className={classes.checkIcon} />}
              {office.name}
            </MenuItem>
          );
        });
    }

    // Render grouped offices with accordion headers FIRST
    sortedGroupNames.forEach((groupName) => {
      const isExpanded = expandedGroups[groupName];

      // Group header (non-selectable)
      items.push(
        <ListSubheader
          key={`group-${groupName}`}
          className={`${classes.groupHeader} ${
            isExpanded
              ? classes.groupHeaderExpanded
              : classes.groupHeaderCollapsed
          }`}
          onClick={(e) => toggleGroup(groupName, e)}
          onMouseDown={(e) => e.preventDefault()} // Prevent menu from closing
          disableSticky
          data-group-header="true"
        >
          <span>{groupName}</span>
          <ChevronRight className={classes.chevronIcon} />
        </ListSubheader>
      );

      // Nested offices (only show if expanded)
      if (isExpanded) {
        groupedOffices[groupName].forEach((office) => {
          const isSelected = office.id === selectedId;
          items.push(
            <MenuItem
              key={office.id}
              value={office.id}
              className={`${classes.nestedMenuItem} ${
                isSelected ? classes.selectedMenuItem : ""
              }`}
            >
              {isSelected && <Check className={classes.checkIcon} />}
              {office.name}
            </MenuItem>
          );
        });
      }
    });

    // Render standalone offices AFTER grouped offices
    standaloneOffices.forEach((office) => {
      const isSelected = office.id === selectedId;
      items.push(
        <MenuItem
          key={office.id}
          value={office.id}
          className={isSelected ? classes.selectedMenuItem : ""}
        >
          {isSelected && <Check className={classes.checkIcon} />}
          {office.name}
        </MenuItem>
      );
    });

    return items;
  };

  return hasManyOffices ? (
    <FormControl className="info__office" variant="filled">
      <label
        className="info-office__label"
        htmlFor={htmlId || "office-select-navbar"}
      >
        Agence
      </label>
      <Select
        name="office"
        disabled={loading}
        open={selectOpen}
        onOpen={() => setSelectOpen(true)}
        onClose={(event) => {
          // Check if click is on a group header
          if (event && event.target) {
            const target = event.target;
            const groupHeader = target.closest('[data-group-header="true"]');
            if (groupHeader) {
              // Don't close if clicking on group header
              return;
            }
          }
          // Close for all other cases
          setSelectOpen(false);
        }}
        value={officeSelectValue.id || ""}
        onChange={(event) => {
          const office = findOffice(event, offices);
          if (office) {
            setOfficeSelectValue(office);
          }
        }}
        displayEmpty
        renderValue={(value) => {
          // Display the selected office name
          if (!value) return "";
          return selectedOfficeName;
        }}
        input={
          <OutlinedInput name="office" id={htmlId || "office-select-navbar"} />
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
          disableAutoFocusItem: false,
          variant: "menu",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          getContentAnchorEl: null,
        }}
      >
        {renderMenuItems()}
      </Select>
    </FormControl>
  ) : (
    <Typography>
      {offices?.length
        ? offices[0].name
        : loading
        ? null
        : "Agence indisponible"}
    </Typography>
  );
};

OfficeSelect.propTypes = {
  htmlId: PropTypes.string,
};

export default OfficeSelect;
